// Cloudflare Turnstile: token anti-bot que /api/chat/start exige cuando el
// bridge tiene TURNSTILE_ENABLED=true (ver middleware/chatStartGate.js).
//
// El widget es "interaction-only": invisible salvo que Cloudflare necesite que
// la persona haga click, y en ese caso aparece sobre el chat (.turnstile-slot).
// Los tokens son de un solo uso, así que cada /start (incluida la renovación
// silenciosa de sesión) renderiza un widget nuevo y lo quita al terminar.

import { TURNSTILE_SITE_KEY } from "../config.js";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
// Holgado: si hace falta interacción, la persona tiene que ver y tocar el widget.
const TOKEN_TIMEOUT_MS = 60_000;

let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile_missing")));
      script.onerror = () => reject(new Error("turnstile_load_failed"));
      document.head.appendChild(script);
    }).catch((err) => {
      // Permitir reintentar la carga en el próximo /start (red intermitente).
      scriptPromise = null;
      throw err;
    });
  }
  return scriptPromise;
}

/**
 * @returns {Promise<string|null>} token para mandar en /start, o null si no hay
 *   site key o no se pudo obtener (el server decide: 403 si lo exige).
 */
export async function getTurnstileToken({ siteKey = TURNSTILE_SITE_KEY } = {}) {
  if (!siteKey) return null;

  let turnstile;
  try {
    turnstile = await loadScript();
  } catch {
    return null;
  }

  const slot = document.createElement("div");
  slot.className = "turnstile-slot";
  document.body.appendChild(slot);

  return new Promise((resolve) => {
    let widgetId = null;
    let settled = false;
    let timer = null;

    const finish = (token) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (widgetId != null) turnstile.remove(widgetId);
      } catch { /* widget ya removido */ }
      slot.remove();
      resolve(token || null);
    };

    timer = setTimeout(() => finish(null), TOKEN_TIMEOUT_MS);

    try {
      widgetId = turnstile.render(slot, {
        sitekey: siteKey,
        action: "chat_start",
        appearance: "interaction-only",
        language: "es",
        callback: (token) => finish(token),
        "error-callback": () => finish(null),
        "expired-callback": () => finish(null),
        "timeout-callback": () => finish(null)
      });
    } catch {
      finish(null);
    }
  });
}
